import { AuthenticationQueryParams } from '@dtos/query-params';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { ClientTrackingName } from '@models/client-tracking-name';
import { SendPinCodeResult } from '@models/send-pin-code-result';
import PinCodeAuthorisationPage from '@screens/review-section3/pincode-authorisation/page';
import { IAuthenticatedUserService } from '@services/authenticated-user-service';
import {
    createAuthenticatedUserService,
    createAuthorisationStore,
    createLoaderService,
    createPinCodeStore,
    createRemoteRepresentativeService,
    createSectionFlowRoutingService,
    createTrackingService,
    createTranslationService
} from '@services/factories';
import { ILoaderService } from '@services/LoaderService';
import { IRemoteRepresentativeService } from '@services/remote-representative-service';
import { ISectionFlowRoutingService } from '@services/section-flow-routing-service';
import { ITrackingService } from '@services/TrackingService';
import { authenticationParamsContext } from '@stores/authentication/authentication-params-context';
import { IAuthorisationStore } from '@stores/authorisation/authorisation-store';
import { IPinCodeStore } from '@stores/authorisation/pin-code-store';
import { cacheTranslations } from '@utils/cache-translations';
import { wait } from '@utils/time-delay';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';

enum PinCodeAuthorisationTranslation {
    sendingPinCode = 'sendingPinCode'
}

const pinCodeSendingMessageDurationMilliseconds = 3000;

@observer
class PinCodeAuthorisation extends React.Component<RouteComponentProps> {
    static readonly translationContext: string = 'pinCodeAuthorisation';
    static contextType = authenticationParamsContext;
    declare context: React.ContextType<typeof authenticationParamsContext>;

    // Probably good idea to dependency inject these later
    pinCodeStore: IPinCodeStore = createPinCodeStore();
    authorisationStore: IAuthorisationStore = createAuthorisationStore();
    loaderService: ILoaderService = createLoaderService();
    remoteRepresentativeService: IRemoteRepresentativeService =
        createRemoteRepresentativeService();
    trackingService: ITrackingService = createTrackingService();
    sectionFlowRoutingService: ISectionFlowRoutingService =
        createSectionFlowRoutingService();
    authenticatedUserService: IAuthenticatedUserService = createAuthenticatedUserService();

    readonly translations = cacheTranslations(
        createTranslationService(),
        PinCodeAuthorisationTranslation,
        PinCodeAuthorisation.translationContext
    );

    @observable
    hadErrorStartingFlow: boolean = false;

    async componentDidMount() {
        // todo: supply the loader registration through a LoaderProvider so that this isn't necessary
        // allow the loader element to register itself
        await wait(0);

        await this.sendInitialPinCode();
    }

    private async sendInitialPinCode() {
        this.loaderService.showLoader(this.translations.sendingPinCode);

        await Promise.all([
            this.pinCodeStore.sendInitialPinCode(this.getQueryParams()),
            wait(pinCodeSendingMessageDurationMilliseconds)
        ]);

        this.loaderService.hideLoader();

        this.trackPinCodeSent(ClientTrackingCategory.SendInitialPinCode);
    }

    private trackPinCodeSent(pinCodeCategory: ClientTrackingCategory) {
        if (this.pinCodeStore.pinCodeResult === null) {
            return;
        }

        const trackingResult = {
            [SendPinCodeResult.Success]: ClientTrackingName.Success,
            [SendPinCodeResult.UnrecognisedError]: ClientTrackingName.Error,
            [SendPinCodeResult.InvalidId]: ClientTrackingName.NotFound
        };

        this.trackingService.trackEvent(
            pinCodeCategory,
            trackingResult[this.pinCodeStore.pinCodeResult]
        );
    }

    getQueryParams(): AuthenticationQueryParams {
        return this.context?.params ?? {};
    }

    getCandidateFullName = (): string => {
        return this.context?.fullName ?? '';
    };

    resendPinCode = async () => {
        this.loaderService.showLoader(this.translations.sendingPinCode);

        await Promise.all([
            this.pinCodeStore.sendPinCodeToWorker(this.getQueryParams()),
            wait(pinCodeSendingMessageDurationMilliseconds)
        ]);

        this.loaderService.hideLoader();
        this.trackPinCodeSent(ClientTrackingCategory.ResendPinCode);
    };

    handlePinCodeFailedAuthentication = () => {
        this.trackingService.trackEvent(
            ClientTrackingCategory.Authorisation,
            ClientTrackingName.Error
        );
    };

    handlePinCodeAuthenticated = async () => {
        this.trackingService.trackEvent(
            ClientTrackingCategory.Authorisation,
            ClientTrackingName.Success
        );

        await this.authenticatedUserService.setupErrorReporting(
            this.getQueryParams().authorizedRepresentativeId ?? ''
        );

        this.hadErrorStartingFlow = false;
        try {
            const routeToStart =
                await this.sectionFlowRoutingService.routeSectionFromStatusAsync();
            this.props.history.push(routeToStart);
            this.trackingService.trackEvent(
                ClientTrackingCategory.I9FlowTypeRequest,
                ClientTrackingName.Success
            );
        } catch (error) {
            // TODO: display some feedback when a flow could not be determined
            this.hadErrorStartingFlow = true;
            this.trackingService.trackEvent(
                ClientTrackingCategory.I9FlowTypeRequest,
                ClientTrackingName.Error
            );
        }
    };

    render() {
        return (
            <PinCodeAuthorisationPage
                pinCodeStore={this.pinCodeStore}
                candidateName={this.getCandidateFullName()}
                onResendPin={this.resendPinCode}
                onPinSuccess={this.handlePinCodeAuthenticated}
                onPinFailure={this.handlePinCodeFailedAuthentication}
            />
        );
    }
}

export default PinCodeAuthorisation;



// WEBPACK FOOTER //
// ./src/screens/review-section3/pincode-authorisation/index.tsx